import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, 
  UserCheck, 
  Building2, 
  RefreshCw,
  AlertCircle,
  Cpu,
  Coins,
  Zap,
  TrendingUp,
  Bot,
  Activity,
  ArrowUpRight,
  Sparkles,
  ShieldCheck,
  ChevronRight,
  Terminal,
  Radio,
  Layers,
  Globe,
  Calendar,
  AppWindow,
  Filter,
  CheckSquare,
  Square,
  ChevronDown,
  X
} from 'lucide-react';
import { dashboardApi } from '../services/dashboardApi';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Skeleton from '../components/ui/Skeleton';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState({ recent_users: [], recent_tenants: [] });
  const [aiCosts, setAiCosts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [activeModelTab, setActiveModelTab] = useState('all');
  const [aiViewTab, setAiViewTab] = useState('app'); // 'app' | 'channel' | 'date' | 'models'
  const [selectedAppIds, setSelectedAppIds] = useState([]);
  const [isAppDropdownOpen, setIsAppDropdownOpen] = useState(false);
  const [aiFiltering, setAiFiltering] = useState(false);
  const appDropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (appDropdownRef.current && !appDropdownRef.current.contains(event.target)) {
        setIsAppDropdownOpen(false);
      }
    };
    if (isAppDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAppDropdownOpen]);

  const fetchAiCostsOnly = useCallback(async (appIds = selectedAppIds) => {
    try {
      setAiFiltering(true);
      const params = {};
      if (appIds && appIds.length > 0) {
        params.app_ids = appIds.join(',');
      }
      const data = await dashboardApi.getAiCosts(params);
      if (data) {
        setAiCosts(data);
      }
    } catch (err) {
      console.error('Failed to filter AI costs:', err);
    } finally {
      setAiFiltering(false);
    }
  }, [selectedAppIds]);

  const handleToggleApp = (appId) => {
    const nextSelection = selectedAppIds.includes(appId)
      ? selectedAppIds.filter(id => id !== appId)
      : [...selectedAppIds, appId];
    setSelectedAppIds(nextSelection);
    fetchAiCostsOnly(nextSelection);
  };

  const handleSelectAllApps = () => {
    setSelectedAppIds([]);
    fetchAiCostsOnly([]);
  };

  const fetchDashboardData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const aiParams = {};
      if (selectedAppIds && selectedAppIds.length > 0) {
        aiParams.app_ids = selectedAppIds.join(',');
      }

      const [statsData, activityData, aiCostsData] = await Promise.all([
        dashboardApi.getStats().catch(err => {
          console.error('Stats error:', err);
          return null;
        }),
        dashboardApi.getRecentActivity().catch(err => {
          console.error('Activity error:', err);
          return { recent_users: [], recent_tenants: [] };
        }),
        dashboardApi.getAiCosts(aiParams).catch(err => {
          console.error('AI Costs error:', err);
          return null;
        })
      ]);

      if (statsData) setStats(statsData);
      if (activityData) setActivity(activityData);
      if (aiCostsData) setAiCosts(aiCostsData);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedAppIds]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchDashboardData(true);
      }, 30000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, fetchDashboardData]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  if (loading) {
    return (
      <div className="space-y-6 max-w-7xl mx-auto p-2">
        <div className="flex justify-between items-center py-3">
          <Skeleton width="220px" height="2rem" />
          <Skeleton width="180px" height="2.25rem" rounded="lg" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="h-32 p-5"><Skeleton className="h-full w-full" /></Card>
          ))}
        </div>
        <Card className="h-72 p-6"><Skeleton className="h-full w-full" /></Card>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="h-96 p-6"><Skeleton className="h-full w-full" /></Card>
          <Card className="h-96 p-6"><Skeleton className="h-full w-full" /></Card>
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="max-w-xl mx-auto mt-16 p-6">
        <EmptyState 
          icon={AlertCircle}
          title="Telemetry Connection Failed"
          description={error}
          action={<Button onClick={() => fetchDashboardData()}>Re-establish Link</Button>}
        />
      </div>
    );
  }

  const trendData = stats?.users?.trend || [];
  const totalTrendRegistrations = trendData.reduce((acc, curr) => acc + (curr.count || 0), 0);
  const maxTrendValue = Math.max(...trendData.map(d => d.count || 0), 1);

  // Filtered AI calls if tab selected
  const recentAiCalls = (aiCosts?.recent_calls || []).filter(call => {
    if (activeModelTab === 'all') return true;
    return call.model === activeModelTab;
  });

  return (
    <div className="space-y-7 pb-16 max-w-7xl mx-auto">
      {/* Futuristic Command Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white p-6 sm:p-8 border border-indigo-500/20 shadow-xl shadow-indigo-950/30">
        <div className="absolute inset-0 bg-grid-pattern opacity-20 pointer-events-none" />
        <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-indigo-500/10 blur-3xl pointer-events-none" />
        <div className="absolute -left-16 -bottom-16 w-64 h-64 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="flex items-center gap-2.5 mb-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wider uppercase bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                <Radio size={12} className="text-emerald-400 animate-pulse" />
                Live Telemetry
              </span>
              <span className="text-xs text-slate-400 font-mono hidden sm:inline">
                Node ID: RODOUD-CORE-PROD
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white flex items-center gap-3">
              Platform Mission Control
              <Sparkles size={20} className="text-indigo-400 hidden sm:inline" />
            </h1>
            <p className="text-sm text-slate-300/80 mt-1 max-w-xl">
              Real-time platform vitals, multi-tenant workload metrics, and neural model expenditure telemetry.
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Glassmorphic Auto-refresh toggle */}
            <div className="flex items-center gap-2.5 bg-white/5 backdrop-blur-md px-3.5 py-2 rounded-xl border border-white/10 shadow-inner">
              <span className="text-xs font-medium text-slate-300 select-none">
                Live Feed
              </span>
              <div 
                className={`relative inline-flex h-5 w-9 cursor-pointer items-center rounded-full transition-colors ${autoRefresh ? 'bg-indigo-500 shadow-sm shadow-indigo-500/50' : 'bg-white/10'}`}
                onClick={() => setAutoRefresh(!autoRefresh)}
                role="button"
                tabIndex={0}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${autoRefresh ? 'translate-x-4' : 'translate-x-1'}`} />
              </div>
            </div>

            <button 
              onClick={() => fetchDashboardData(true)}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 active:scale-95 text-white text-xs font-semibold tracking-wide transition-all shadow-lg shadow-indigo-600/30 disabled:opacity-50"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
              <span>Sync Now</span>
            </button>
          </div>
        </div>

        {/* Quick Vitals Ribbon */}
        <div className="relative z-10 grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 pt-6 border-t border-white/10 text-xs">
          <div className="flex items-center gap-2 text-slate-300">
            <div className="w-2 h-2 rounded-full bg-emerald-400" />
            <span>API Gateway: <strong className="text-white font-mono">100% Operational</strong></span>
          </div>
          <div className="flex items-center gap-2 text-slate-300">
            <Cpu size={14} className="text-indigo-400" />
            <span>LLM Clusters: <strong className="text-white font-mono">{aiCosts?.by_model?.length || 3} Active</strong></span>
          </div>
          <div className="flex items-center gap-2 text-slate-300">
            <ShieldCheck size={14} className="text-cyan-400" />
            <span>Tenants Isolated: <strong className="text-white font-mono">{stats?.tenants?.total || 0}</strong></span>
          </div>
          <div className="flex items-center gap-2 text-slate-300">
            <Activity size={14} className="text-amber-400" />
            <span>Sync Latency: <strong className="text-white font-mono">&lt; 24ms</strong></span>
          </div>
        </div>
      </div>

      {/* Primary KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Total Users */}
        <div className="relative overflow-hidden rounded-2xl p-5 bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow-sm hover:shadow-md transition-all group">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Registered Identity</span>
            <div className="p-2.5 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 group-hover:scale-110 transition-transform">
              <Users size={18} />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-slate-900 dark:text-white font-mono tracking-tight">
              {(stats?.users?.total || 0).toLocaleString()}
            </span>
            <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 flex items-center">
              <ArrowUpRight size={14} /> +{(stats?.users?.recent || 0)} new
            </span>
          </div>
          <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400 border-t border-slate-100 dark:border-slate-800/80 pt-2.5">
            <span>Active base: <strong>{stats?.users?.active || 0}</strong></span>
            <Link to="/users" className="text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-0.5">
              Manage <ChevronRight size={12} />
            </Link>
          </div>
        </div>

        {/* Card 2: Active Users */}
        <div className="relative overflow-hidden rounded-2xl p-5 bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow-sm hover:shadow-md transition-all group">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Active Operators</span>
            <div className="p-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform">
              <UserCheck size={18} />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-slate-900 dark:text-white font-mono tracking-tight">
              {(stats?.users?.active || 0).toLocaleString()}
            </span>
            <span className="text-xs text-slate-500 font-mono">
              / {(stats?.users?.total || 0)} ({stats?.users?.total ? Math.round(((stats.users.active || 0) / stats.users.total) * 100) : 0}%)
            </span>
          </div>
          <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400 border-t border-slate-100 dark:border-slate-800/80 pt-2.5">
            <span>Inactive count: <strong>{(stats?.users?.total || 0) - (stats?.users?.active || 0)}</strong></span>
            <span className="text-emerald-600 dark:text-emerald-400 font-medium">Healthy</span>
          </div>
        </div>

        {/* Card 3: Workspaces / Tenants */}
        <div className="relative overflow-hidden rounded-2xl p-5 bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow-sm hover:shadow-md transition-all group">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Tenant Ecosystem</span>
            <div className="p-2.5 rounded-xl bg-cyan-50 dark:bg-cyan-950/60 text-cyan-600 dark:text-cyan-400 group-hover:scale-110 transition-transform">
              <Building2 size={18} />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-slate-900 dark:text-white font-mono tracking-tight">
              {(stats?.tenants?.total || 0).toLocaleString()}
            </span>
            <span className="text-xs font-medium text-slate-500">workspaces</span>
          </div>
          <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400 border-t border-slate-100 dark:border-slate-800/80 pt-2.5">
            <span>Avg users/tenant: <strong>{stats?.tenants?.total ? Math.round(((stats?.users?.total || 0) / stats.tenants.total) * 10) / 10 : 0}</strong></span>
            <Link to="/tenants" className="text-cyan-600 dark:text-cyan-400 hover:underline flex items-center gap-0.5">
              Explore <ChevronRight size={12} />
            </Link>
          </div>
        </div>

        {/* Card 4: AI Telemetry Quick Stat */}
        <div className="relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br from-indigo-50/50 via-white to-purple-50/30 dark:from-slate-900 dark:via-slate-900 dark:to-indigo-950/30 border border-indigo-200/60 dark:border-indigo-900/40 shadow-sm hover:shadow-md transition-all group">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-indigo-700 dark:text-indigo-400 uppercase tracking-wider flex items-center gap-1.5">
              AI Total Cost
              {selectedAppIds.length > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 normal-case">
                  {selectedAppIds.length} app{selectedAppIds.length > 1 ? 's' : ''}
                </span>
              )}
            </span>
            <div className="p-2.5 rounded-xl bg-indigo-600 text-white shadow-md shadow-indigo-600/30 group-hover:scale-110 transition-transform">
              <Coins size={18} />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-slate-900 dark:text-white font-mono tracking-tight">
              ${Number(aiCosts?.summary?.total_cost || 0).toFixed(4)}
            </span>
          </div>
          <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400 border-t border-slate-100 dark:border-slate-800/80 pt-2.5">
            <span><strong>{Number(aiCosts?.summary?.total_tokens || 0).toLocaleString()}</strong> tokens</span>
            <span className="text-indigo-600 dark:text-indigo-400 font-medium font-mono">{aiCosts?.summary?.total_calls || 0} reqs</span>
          </div>
        </div>
      </div>

      {/* Advanced AI Cost & Multi-Dimensional Intelligence Deck */}
      {aiCosts?.summary && (
        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          {/* Deck Header */}
          <div className="p-5 sm:p-6 border-b border-slate-200 dark:border-slate-800 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 bg-slate-50/50 dark:bg-slate-800/30">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-500/20">
                <Cpu size={20} />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  AI Consumption & Expenditure Telemetry
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-300 uppercase">
                    Live Diagnostics
                  </span>
                  {aiFiltering && (
                    <RefreshCw size={13} className="animate-spin text-indigo-600 dark:text-indigo-400" />
                  )}
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Granular cost attribution by App, Channel, Daily Timeline, and Neural Models
                </p>
              </div>
            </div>

            {/* Right Controls: App Multi-Select Filter & View Switcher Tabs */}
            <div className="flex flex-wrap items-center gap-2.5">
              {/* App Filter Multi-Select Dropdown */}
              <div className="relative" ref={appDropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsAppDropdownOpen(prev => !prev)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all shadow-sm ${
                    selectedAppIds.length > 0
                      ? 'bg-indigo-50 dark:bg-indigo-950/60 border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300 ring-2 ring-indigo-500/20'
                      : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-750'
                  }`}
                  title="Filter AI telemetry by applications"
                >
                  <Filter size={13} className={selectedAppIds.length > 0 ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-500'} />
                  <span>
                    {selectedAppIds.length === 0
                      ? 'All Apps'
                      : `${selectedAppIds.length} App${selectedAppIds.length > 1 ? 's' : ''}`}
                  </span>
                  {selectedAppIds.length > 0 && (
                    <span className="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-indigo-600 text-white">
                      {selectedAppIds.length}
                    </span>
                  )}
                  <ChevronDown size={13} className={`text-slate-400 transition-transform ${isAppDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isAppDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-72 sm:w-80 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xl z-50 overflow-hidden">
                    <div className="p-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/70 dark:bg-slate-800/50">
                      <div className="flex items-center gap-2">
                        <Filter size={13} className="text-indigo-600 dark:text-indigo-400" />
                        <span className="text-xs font-bold text-slate-800 dark:text-slate-200">Filter by Application</span>
                      </div>
                      <div className="flex items-center gap-2 text-[11px]">
                        <button
                          type="button"
                          onClick={handleSelectAllApps}
                          className="text-indigo-600 dark:text-indigo-400 hover:underline font-semibold"
                        >
                          All
                        </button>
                        <span className="text-slate-300 dark:text-slate-700">|</span>
                        <button
                          type="button"
                          onClick={handleSelectAllApps}
                          className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                        >
                          Reset
                        </button>
                      </div>
                    </div>

                    <div className="max-h-64 overflow-y-auto p-2 space-y-1">
                      {/* "All Applications" option */}
                      <button
                        type="button"
                        onClick={handleSelectAllApps}
                        className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-xs transition-colors text-left ${
                          selectedAppIds.length === 0
                            ? 'bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 font-semibold'
                            : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                        }`}
                      >
                        <div className="flex items-center gap-2.5">
                          {selectedAppIds.length === 0 ? (
                            <CheckSquare size={14} className="text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                          ) : (
                            <Square size={14} className="text-slate-400 flex-shrink-0" />
                          )}
                          <span className="truncate">All Applications</span>
                        </div>
                        <span className="text-[10px] text-slate-400 font-mono">
                          {aiCosts.available_apps?.length || 0} total
                        </span>
                      </button>

                      {/* Individual apps */}
                      {(aiCosts.available_apps || []).map((app) => {
                        const isSelected = selectedAppIds.includes(app.app_id);
                        return (
                          <button
                            key={app.app_id}
                            type="button"
                            onClick={() => handleToggleApp(app.app_id)}
                            className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-xs transition-colors text-left ${
                              isSelected
                                ? 'bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 font-semibold'
                                : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                            }`}
                          >
                            <div className="flex items-center gap-2.5 min-w-0 pr-2">
                              {isSelected ? (
                                <CheckSquare size={14} className="text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                              ) : (
                                <Square size={14} className="text-slate-400 flex-shrink-0" />
                              )}
                              <div className="truncate">
                                <div className="truncate font-medium">{app.app_name}</div>
                                <div className="text-[10px] text-slate-400 flex items-center gap-1.5 font-mono">
                                  <span>{app.app_mode || 'api'}</span>
                                  <span>•</span>
                                  <span>${Number(app.cost || 0).toFixed(4)}</span>
                                </div>
                              </div>
                            </div>
                            <span className="text-[10px] text-slate-400 font-mono flex-shrink-0">
                              {Number(app.calls || 0).toLocaleString()} reqs
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    {selectedAppIds.length > 0 && (
                      <div className="p-2 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 text-center">
                        <span className="text-[11px] text-indigo-600 dark:text-indigo-400 font-medium">
                          Filtering AI Telemetry by {selectedAppIds.length} app{selectedAppIds.length > 1 ? 's' : ''}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* View Switcher Tabs */}
              <div className="flex items-center gap-1.5 overflow-x-auto p-1 bg-slate-200/60 dark:bg-slate-800/80 rounded-xl border border-slate-300/40 dark:border-slate-700/60">
                <button
                  onClick={() => setAiViewTab('app')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    aiViewTab === 'app'
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
                  }`}
                >
                  <AppWindow size={13} />
                  <span>By App ({aiCosts.by_app?.length || 0})</span>
                </button>

                <button
                  onClick={() => setAiViewTab('channel')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    aiViewTab === 'channel'
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
                  }`}
                >
                  <Globe size={13} />
                  <span>By Channel ({aiCosts.by_channel?.length || 0})</span>
                </button>

                <button
                  onClick={() => setAiViewTab('date')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    aiViewTab === 'date'
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
                  }`}
                >
                  <Calendar size={13} />
                  <span>By Date ({aiCosts.trend?.length || 0}d)</span>
                </button>

                <button
                  onClick={() => setAiViewTab('models')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    aiViewTab === 'models'
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
                  }`}
                >
                  <Bot size={13} />
                  <span>Models & Efficiency</span>
                </button>
              </div>
            </div>
          </div>

          {/* Active Filter Bar (when any app is selected) */}
          {selectedAppIds.length > 0 && (
            <div className="px-5 py-2.5 bg-indigo-50/70 dark:bg-indigo-950/40 border-b border-indigo-100 dark:border-indigo-900/50 flex flex-wrap items-center justify-between gap-2 text-xs">
              <div className="flex items-center gap-2 text-indigo-900 dark:text-indigo-200 flex-wrap">
                <Filter size={13} className="text-indigo-600 dark:text-indigo-400" />
                <span className="font-semibold">Filtered by {selectedAppIds.length} app{selectedAppIds.length > 1 ? 's' : ''}:</span>
                <div className="flex flex-wrap gap-1.5">
                  {(aiCosts.available_apps || [])
                    .filter(a => selectedAppIds.includes(a.app_id))
                    .map(app => (
                      <span
                        key={app.app_id}
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium bg-white dark:bg-slate-900 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 shadow-xs"
                      >
                        <span>{app.app_name}</span>
                        <button
                          type="button"
                          onClick={() => handleToggleApp(app.app_id)}
                          className="hover:text-red-500 transition-colors p-0.5"
                          title="Remove this app from filter"
                        >
                          <X size={10} />
                        </button>
                      </span>
                    ))}
                </div>
              </div>
              <button
                type="button"
                onClick={handleSelectAllApps}
                className="text-[11px] font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 underline"
              >
                Clear all filters
              </button>
            </div>
          )}

          <div className="p-5 sm:p-6 space-y-6">
            {/* VIEW 1: BY APP */}
            {aiViewTab === 'app' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(aiCosts.by_app || []).map((app, idx) => {
                    const totalCost = Number(aiCosts.summary.total_cost || 0.0001);
                    const appCost = Number(app.cost || 0);
                    const costPct = totalCost > 0 ? ((appCost / totalCost) * 100).toFixed(1) : 0;

                    return (
                      <div key={idx} className="p-4 rounded-xl bg-slate-50/80 dark:bg-slate-800/40 border border-slate-200/80 dark:border-slate-700/60 flex flex-col justify-between space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <h4 className="font-bold text-sm text-slate-900 dark:text-white truncate" title={app.app_name}>
                              {app.app_name}
                            </h4>
                            <div className="flex items-center gap-1.5 mt-1">
                              <span className="px-2 py-0.5 rounded text-[10px] font-extrabold uppercase tracking-wider bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                                {app.app_mode || 'api'}
                              </span>
                              <span className="text-[10px] text-slate-400 font-mono">
                                ID: {app.app_id ? `${app.app_id.substring(0, 8)}...` : 'system'}
                              </span>
                            </div>
                          </div>
                          <span className="px-2 py-1 rounded-lg text-xs font-mono font-bold bg-white dark:bg-slate-900 text-emerald-600 dark:text-emerald-400 border border-slate-200 dark:border-slate-700 shadow-sm">
                            ${appCost.toFixed(5)}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-xs font-mono pt-2 border-t border-slate-200/60 dark:border-slate-700/40">
                          <div>
                            <span className="text-[10px] text-slate-400 uppercase block">Invocations</span>
                            <span className="font-bold text-slate-800 dark:text-slate-200">{app.calls} reqs</span>
                          </div>
                          <div className="text-right">
                            <span className="text-[10px] text-slate-400 uppercase block">Tokens</span>
                            <span className="font-bold text-indigo-600 dark:text-indigo-400">{Number(app.tokens || 0).toLocaleString()}</span>
                          </div>
                        </div>

                        <div className="space-y-1">
                          <div className="flex justify-between text-[10px] text-slate-500 dark:text-slate-400">
                            <span>Cost Share</span>
                            <span className="font-semibold">{costPct}%</span>
                          </div>
                          <div className="w-full bg-slate-200 dark:bg-slate-700 h-1.5 rounded-full overflow-hidden">
                            <div 
                              className="bg-indigo-500 h-full rounded-full transition-all" 
                              style={{ width: `${Math.max(Number(costPct), 2)}%` }} 
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Detailed App Summary Table */}
                <div className="mt-4 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 dark:bg-slate-800/80 text-slate-500 dark:text-slate-400 uppercase text-[10px] tracking-wider border-b border-slate-200 dark:border-slate-800">
                      <tr>
                        <th className="px-4 py-2.5">Application Name</th>
                        <th className="px-4 py-2.5">Mode</th>
                        <th className="px-4 py-2.5">Calls</th>
                        <th className="px-4 py-2.5">Total Tokens</th>
                        <th className="px-4 py-2.5">Cost (USD)</th>
                        <th className="px-4 py-2.5 text-right">% Spend</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-mono">
                      {(aiCosts.by_app || []).map((app, idx) => {
                        const totalCost = Number(aiCosts.summary.total_cost || 0.0001);
                        const appCost = Number(app.cost || 0);
                        const costPct = totalCost > 0 ? ((appCost / totalCost) * 100).toFixed(1) : 0;
                        return (
                          <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/40">
                            <td className="px-4 py-3 font-bold text-slate-900 dark:text-white font-sans flex items-center gap-2">
                              <AppWindow size={14} className="text-indigo-500 flex-shrink-0" />
                              <span>{app.app_name}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                                {app.app_mode}
                              </span>
                            </td>
                            <td className="px-4 py-3 font-semibold text-slate-800 dark:text-slate-200">
                              {app.calls}
                            </td>
                            <td className="px-4 py-3 text-indigo-600 dark:text-indigo-400 font-semibold">
                              {Number(app.tokens || 0).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-emerald-600 dark:text-emerald-400 font-bold">
                              ${appCost.toFixed(5)}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">
                              {costPct}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* VIEW 2: BY CHANNEL */}
            {aiViewTab === 'channel' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {(aiCosts.by_channel || []).map((ch, idx) => {
                    const totalCost = Number(aiCosts.summary.total_cost || 0.0001);
                    const chCost = Number(ch.cost || 0);
                    const costPct = totalCost > 0 ? ((chCost / totalCost) * 100).toFixed(1) : 0;

                    const getChannelMeta = (c) => {
                      switch ((c || '').toLowerCase()) {
                        case 'website':
                          return {
                            label: 'Website',
                            icon: <Globe size={18} className="text-emerald-500" />,
                            badge: 'Web Widget',
                            accent: 'from-emerald-500 to-teal-600',
                            border: 'border-emerald-200 dark:border-emerald-800/60'
                          };
                        case 'facebook':
                          return {
                            label: 'Facebook',
                            icon: (
                              <svg className="w-4 h-4 fill-[#1877F2]" viewBox="0 0 24 24">
                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                              </svg>
                            ),
                            badge: 'Meta / Messenger',
                            accent: 'from-blue-500 to-indigo-600',
                            border: 'border-blue-200 dark:border-blue-800/60'
                          };
                        case 'instagram':
                          return {
                            label: 'Instagram',
                            icon: (
                              <svg className="w-4 h-4 fill-[#E4405F]" viewBox="0 0 24 24">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                              </svg>
                            ),
                            badge: 'Direct & Comments',
                            accent: 'from-pink-500 to-rose-600',
                            border: 'border-pink-200 dark:border-pink-800/60'
                          };
                        case 'whatsapp':
                          return {
                            label: 'WhatsApp',
                            icon: (
                              <svg className="w-4 h-4 fill-[#25D366]" viewBox="0 0 24 24">
                                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                              </svg>
                            ),
                            badge: 'Cloud API',
                            accent: 'from-emerald-600 to-green-500',
                            border: 'border-emerald-200 dark:border-emerald-800/60'
                          };
                        default:
                          return {
                            label: c || 'Direct API',
                            icon: <Terminal size={18} className="text-indigo-500" />,
                            badge: 'System Gateway',
                            accent: 'from-indigo-500 to-purple-600',
                            border: 'border-slate-200 dark:border-slate-700/60'
                          };
                      }
                    };

                    const meta = getChannelMeta(ch.channel);

                    return (
                      <div key={idx} className={`p-4 rounded-xl bg-slate-50/80 dark:bg-slate-800/40 border ${meta.border} flex flex-col justify-between space-y-3 transition-all hover:shadow-md`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2.5">
                            <div className="p-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-sm flex-shrink-0">
                              {meta.icon}
                            </div>
                            <div>
                              <h4 className="font-bold text-sm text-slate-900 dark:text-white">
                                {meta.label}
                              </h4>
                              <span className="text-[10px] text-slate-500 dark:text-slate-400 font-mono">
                                {meta.badge}
                              </span>
                            </div>
                          </div>
                          <span className="px-2 py-1 rounded-lg text-xs font-mono font-bold bg-white dark:bg-slate-900 text-emerald-600 dark:text-emerald-400 border border-slate-200 dark:border-slate-700 shadow-sm">
                            ${chCost.toFixed(5)}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-xs font-mono pt-2 border-t border-slate-200/60 dark:border-slate-700/40">
                          <div>
                            <span className="text-[10px] text-slate-400 uppercase block">Invocations</span>
                            <span className="font-bold text-slate-800 dark:text-slate-200">{ch.calls} reqs</span>
                          </div>
                          <div className="text-right">
                            <span className="text-[10px] text-slate-400 uppercase block">Volume</span>
                            <span className="font-bold text-indigo-600 dark:text-indigo-400">{Number(ch.tokens || 0).toLocaleString()} tk</span>
                          </div>
                        </div>

                        <div className="space-y-1">
                          <div className="flex justify-between text-[10px] text-slate-500 dark:text-slate-400">
                            <span>Channel Share</span>
                            <span className="font-semibold">{costPct}%</span>
                          </div>
                          <div className="w-full bg-slate-200 dark:bg-slate-700 h-1.5 rounded-full overflow-hidden">
                            <div 
                              className={`bg-gradient-to-r ${meta.accent} h-full rounded-full transition-all`} 
                              style={{ width: `${Math.max(Number(costPct), ch.calls > 0 ? 3 : 0)}%` }} 
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Comparative Channel Breakdown Table */}
                <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden mt-4">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 dark:bg-slate-800/80 text-slate-500 dark:text-slate-400 uppercase text-[10px] tracking-wider border-b border-slate-200 dark:border-slate-800">
                      <tr>
                        <th className="px-4 py-2.5">Channel Gateway</th>
                        <th className="px-4 py-2.5">Type</th>
                        <th className="px-4 py-2.5">Invocations</th>
                        <th className="px-4 py-2.5">Token Volume</th>
                        <th className="px-4 py-2.5">Total Spend (USD)</th>
                        <th className="px-4 py-2.5 text-right">% of AI Budget</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-mono">
                      {(aiCosts.by_channel || []).map((ch, idx) => {
                        const totalCost = Number(aiCosts.summary.total_cost || 0.0001);
                        const chCost = Number(ch.cost || 0);
                        const costPct = totalCost > 0 ? ((chCost / totalCost) * 100).toFixed(1) : 0;

                        return (
                          <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/40">
                            <td className="px-4 py-3 font-bold text-slate-900 dark:text-white capitalize font-sans">
                              {ch.channel}
                            </td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                                {ch.channel === 'website' ? 'Web Client' : ch.channel === 'facebook' ? 'Meta Messenger' : ch.channel === 'instagram' ? 'Meta IG' : 'Meta WA'}
                              </span>
                            </td>
                            <td className="px-4 py-3 font-semibold text-slate-800 dark:text-slate-200">
                              {ch.calls} reqs
                            </td>
                            <td className="px-4 py-3 text-indigo-600 dark:text-indigo-400 font-semibold">
                              {Number(ch.tokens || 0).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-emerald-600 dark:text-emerald-400 font-bold">
                              ${chCost.toFixed(5)}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">
                              {costPct}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* VIEW 3: BY DATE (DAILY TIMELINE) */}
            {aiViewTab === 'date' && (
              <div className="space-y-5">
                {/* Visual Daily Velocity Bar Chart */}
                <div className="p-4 rounded-xl bg-slate-50/80 dark:bg-slate-800/40 border border-slate-200/80 dark:border-slate-700/60">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 flex items-center gap-2">
                      <Calendar size={14} className="text-indigo-500" />
                      Daily Consumption Velocity Timeline
                    </h4>
                    <span className="text-xs text-slate-400 font-mono">
                      Last {aiCosts.trend?.length || 0} Recorded Days
                    </span>
                  </div>

                  <div className="h-36 flex items-end gap-2 sm:gap-4 pt-6 pb-2 px-2 overflow-x-auto">
                    {(() => {
                      const trend = aiCosts.trend || [];
                      const maxDailyTokens = Math.max(...trend.map(t => Number(t.tokens || 0)), 1);

                      return trend.map((day, idx) => {
                        const tokenCount = Number(day.tokens || 0);
                        const heightPct = Math.max(Math.round((tokenCount / maxDailyTokens) * 100), 10);
                        const costNum = Number(day.cost || 0);

                        return (
                          <div key={idx} className="flex-1 min-w-[50px] flex flex-col items-center gap-1 group">
                            <span className="text-[10px] font-mono font-semibold text-slate-500 dark:text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                              ${costNum.toFixed(4)}
                            </span>
                            <div className="w-full bg-slate-200 dark:bg-slate-700/60 rounded-t-lg h-24 flex items-end p-1">
                              <div 
                                className="w-full bg-gradient-to-t from-indigo-600 to-indigo-400 group-hover:from-indigo-500 group-hover:to-purple-500 rounded-md transition-all duration-300"
                                style={{ height: `${heightPct}%` }}
                                title={`${day.date}: ${tokenCount.toLocaleString()} tokens, ${day.calls} calls, $${costNum.toFixed(5)}`}
                              />
                            </div>
                            <span className="text-[10px] font-mono text-slate-600 dark:text-slate-400 font-medium">
                              {day.date ? day.date.substring(5) : '--'}
                            </span>
                            <span className="text-[9px] font-mono text-slate-400">
                              {day.calls} calls
                            </span>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>

                {/* Daily Breakdown Table */}
                <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 dark:bg-slate-800/80 text-slate-500 dark:text-slate-400 uppercase text-[10px] tracking-wider border-b border-slate-200 dark:border-slate-800">
                      <tr>
                        <th className="px-4 py-2.5">Date</th>
                        <th className="px-4 py-2.5">Invocations</th>
                        <th className="px-4 py-2.5">Input (Prompt)</th>
                        <th className="px-4 py-2.5">Output (Completion)</th>
                        <th className="px-4 py-2.5">Total Tokens</th>
                        <th className="px-4 py-2.5 text-right">Cost (USD)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-mono">
                      {(aiCosts.trend || []).map((row, idx) => (
                        <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/40">
                          <td className="px-4 py-2.5 font-bold text-slate-900 dark:text-white">
                            {row.date}
                          </td>
                          <td className="px-4 py-2.5 text-slate-700 dark:text-slate-300">
                            {row.calls} calls
                          </td>
                          <td className="px-4 py-2.5 text-slate-500 dark:text-slate-400">
                            {Number(row.input_tokens || 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-2.5 text-slate-500 dark:text-slate-400">
                            {Number(row.output_tokens || 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-2.5 text-indigo-600 dark:text-indigo-400 font-semibold">
                            {Number(row.tokens || 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-2.5 text-right font-bold text-emerald-600 dark:text-emerald-400">
                            ${Number(row.cost || 0).toFixed(5)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* VIEW 4: BY MODEL & RATIO */}
            {aiViewTab === 'models' && (
              <div className="space-y-6">
                {/* 3-Column Metrics Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Token Split */}
                  <div className="p-4 rounded-xl bg-slate-50/80 dark:bg-slate-800/40 border border-slate-200/80 dark:border-slate-700/60">
                    <div className="flex items-center justify-between mb-2 text-xs font-semibold text-slate-500 dark:text-slate-400">
                      <span>Input / Output Token Ratio</span>
                      <Zap size={14} className="text-amber-500" />
                    </div>
                    <div className="flex items-baseline justify-between mb-2">
                      <div className="text-lg font-bold font-mono text-slate-900 dark:text-white">
                        {Number(aiCosts.summary.total_input_tokens || 0).toLocaleString()}
                        <span className="text-xs text-slate-400 font-normal ml-1">in</span>
                      </div>
                      <div className="text-lg font-bold font-mono text-indigo-600 dark:text-indigo-400">
                        {Number(aiCosts.summary.total_output_tokens || 0).toLocaleString()}
                        <span className="text-xs text-slate-400 font-normal ml-1">out</span>
                      </div>
                    </div>
                    {/* Ratio bar */}
                    <div className="w-full bg-slate-200 dark:bg-slate-700 h-2 rounded-full overflow-hidden flex">
                      <div 
                        className="bg-slate-500 h-full"
                        style={{ width: `${Math.round((Number(aiCosts.summary.total_input_tokens || 1) / Math.max(Number(aiCosts.summary.total_tokens || 1), 1)) * 100)}%` }}
                        title="Input Prompt Tokens"
                      />
                      <div 
                        className="bg-indigo-500 h-full flex-1"
                        title="Output Generation Tokens"
                      />
                    </div>
                    <div className="flex justify-between text-[10px] text-slate-400 mt-1.5">
                      <span>Prompt: {Math.round((Number(aiCosts.summary.total_input_tokens || 1) / Math.max(Number(aiCosts.summary.total_tokens || 1), 1)) * 100)}%</span>
                      <span>Completion: {Math.round((Number(aiCosts.summary.total_output_tokens || 0) / Math.max(Number(aiCosts.summary.total_tokens || 1), 1)) * 100)}%</span>
                    </div>
                  </div>

                  {/* Average Cost per Request */}
                  <div className="p-4 rounded-xl bg-slate-50/80 dark:bg-slate-800/40 border border-slate-200/80 dark:border-slate-700/60">
                    <div className="flex items-center justify-between mb-2 text-xs font-semibold text-slate-500 dark:text-slate-400">
                      <span>Inference Efficiency</span>
                      <TrendingUp size={14} className="text-emerald-500" />
                    </div>
                    <div className="text-2xl font-bold font-mono text-slate-900 dark:text-white">
                      ${aiCosts.summary.total_calls ? (Number(aiCosts.summary.total_cost) / Number(aiCosts.summary.total_calls)).toFixed(5) : '0.00000'}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Average payload: <strong className="font-mono text-slate-700 dark:text-slate-300">{aiCosts.summary.avg_tokens_per_call}</strong> tokens/call
                    </p>
                    <div className="mt-2 text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
                      Optimized for sub-cent production conversational workloads
                    </div>
                  </div>

                  {/* Module Attribution */}
                  <div className="p-4 rounded-xl bg-slate-50/80 dark:bg-slate-800/40 border border-slate-200/80 dark:border-slate-700/60">
                    <div className="flex items-center justify-between mb-2 text-xs font-semibold text-slate-500 dark:text-slate-400">
                      <span>Module Spend Breakdown</span>
                      <Layers size={14} className="text-indigo-500" />
                    </div>
                    <div className="space-y-1.5">
                      {aiCosts.by_module?.map((mod, i) => (
                        <div key={i} className="flex items-center justify-between text-xs">
                          <span className="capitalize text-slate-700 dark:text-slate-300 font-medium">{mod.module}</span>
                          <span className="font-mono text-slate-900 dark:text-white font-semibold">
                            ${Number(mod.cost).toFixed(5)} <span className="text-[10px] text-slate-400 font-normal">({mod.calls} reqs)</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Model Consumption Bars */}
                <div className="space-y-3 pt-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Deployed Models Consumption Scale
                    </h4>
                    {/* Model filter tabs */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setActiveModelTab('all')}
                        className={`px-2 py-0.5 rounded text-[11px] font-mono ${
                          activeModelTab === 'all'
                            ? 'bg-indigo-600 text-white'
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                        }`}
                      >
                        All
                      </button>
                      {aiCosts.by_model?.map(m => (
                        <button
                          key={m.model}
                          onClick={() => setActiveModelTab(m.model)}
                          className={`px-2 py-0.5 rounded text-[11px] font-mono ${
                            activeModelTab === m.model
                              ? 'bg-indigo-600 text-white'
                              : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                          }`}
                        >
                          {m.model}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2.5">
                    {aiCosts.by_model?.map((m, idx) => {
                      const tokenPct = ((Number(m.tokens) / Math.max(Number(aiCosts.summary.total_tokens), 1)) * 100).toFixed(1);
                      return (
                        <div key={idx} className="p-3 rounded-xl bg-slate-50/60 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between text-xs gap-1 mb-1.5">
                            <div className="flex items-center gap-2">
                              <Bot size={15} className="text-indigo-500" />
                              <span className="font-bold text-slate-900 dark:text-white font-mono">{m.model}</span>
                            </div>
                            <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400 font-mono text-[11px]">
                              <span>{Number(m.tokens).toLocaleString()} tokens ({tokenPct}%)</span>
                              <span>•</span>
                              <span className="font-semibold text-slate-900 dark:text-slate-200">${Number(m.cost).toFixed(5)}</span>
                              <span>•</span>
                              <span>{m.calls} invocations</span>
                            </div>
                          </div>
                          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
                            <div 
                              className="bg-gradient-to-r from-indigo-500 to-purple-600 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${Math.max(Number(tokenPct), 2)}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Recent Inference Calls Log (Always Visible with App & Channel attribution) */}
            {recentAiCalls.length > 0 && (
              <div className="pt-2 border-t border-slate-100 dark:border-slate-800/80">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                    <Terminal size={14} />
                    Recent Neural Inferences Log (With App & Channel)
                  </h4>
                  <span className="text-xs text-slate-400 font-mono">
                    Showing {Math.min(recentAiCalls.length, 10)} latest events
                  </span>
                </div>

                <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-50 dark:bg-slate-800/80 text-slate-500 dark:text-slate-400 uppercase text-[10px] tracking-wider border-b border-slate-200 dark:border-slate-800">
                        <tr>
                          <th className="px-4 py-2.5">Call ID</th>
                          <th className="px-4 py-2.5">Model</th>
                          <th className="px-4 py-2.5">App</th>
                          <th className="px-4 py-2.5">Channel</th>
                          <th className="px-4 py-2.5">Tokens</th>
                          <th className="px-4 py-2.5">Cost</th>
                          <th className="px-4 py-2.5 text-right">Time</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-mono">
                        {recentAiCalls.slice(0, 10).map(call => (
                          <tr key={call.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/40 transition-colors">
                            <td className="px-4 py-2.5 font-semibold text-slate-800 dark:text-slate-200">
                              {call.id?.substring(0, 13)}...
                            </td>
                            <td className="px-4 py-2.5">
                              <span className="px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 text-[11px]">
                                {call.model}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 font-sans font-medium text-slate-900 dark:text-slate-100">
                              {call.app_name || 'Direct System'}
                            </td>
                            <td className="px-4 py-2.5">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                                {call.channel || 'direct_api'}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">
                              {call.total_tokens || 0} tk
                            </td>
                            <td className="px-4 py-2.5 font-semibold text-emerald-600 dark:text-emerald-400">
                              ${Number(call.cost || 0).toFixed(6)}
                            </td>
                            <td className="px-4 py-2.5 text-right text-slate-400 font-sans text-[11px]">
                              {formatRelativeTime(call.created_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 7-Day Trend Velocity Chart */}
      <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 sm:p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6">
          <div>
            <h3 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
              Registration Velocity (Rolling 7-Day Window)
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {totalTrendRegistrations > 0 
                ? `${totalTrendRegistrations} total operator accounts onboarded across the last 7 days` 
                : 'Platform baseline active • Historical registration trend'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
              {stats?.users?.total || 0} Total Lifetime
            </span>
          </div>
        </div>

        {trendData.length > 0 ? (
          <div className="pt-2">
            <div className="flex items-end justify-between h-48 gap-2 sm:gap-4 px-2 border-b border-slate-200 dark:border-slate-800 pb-3">
              {trendData.map((day, idx) => {
                const count = day.count || 0;
                const heightPercentage = totalTrendRegistrations > 0 
                  ? Math.round((count / maxTrendValue) * 100)
                  : 6;
                const date = new Date(day.date);
                const dayLabel = isNaN(date.getTime()) ? day.date : new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(date);
                const isToday = idx === trendData.length - 1;

                return (
                  <div key={idx} className="flex-1 flex flex-col items-center group relative h-full">
                    <div className="w-full flex-1 flex items-end justify-center">
                      <div 
                        className={`w-full max-w-[52px] rounded-t-xl transition-all duration-300 relative cursor-pointer ${
                          count > 0
                            ? (isToday 
                                ? 'bg-gradient-to-t from-indigo-600 via-indigo-500 to-cyan-400 shadow-lg shadow-indigo-500/30' 
                                : 'bg-indigo-500/80 hover:bg-indigo-600')
                            : 'bg-slate-100 dark:bg-slate-800/60 hover:bg-slate-200 dark:hover:bg-slate-700/80'
                        }`}
                        style={{ height: `${Math.max(heightPercentage, 10)}%` }}
                      >
                        {/* Tooltip */}
                        <div className="opacity-0 group-hover:opacity-100 pointer-events-none absolute -top-10 left-1/2 -translate-x-1/2 bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 text-[11px] font-bold py-1 px-2.5 rounded-lg shadow-xl whitespace-nowrap transition-all z-20 font-mono">
                          {count} {count === 1 ? 'user' : 'users'}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 text-xs font-semibold text-slate-500 dark:text-slate-400 text-center">
                      {dayLabel}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="py-12 text-center text-sm text-slate-400">
            No historical telemetry recorded for the past 7 days.
          </div>
        )}
      </div>

      {/* 2-Column Feeds: Recent Users & Recent Tenants */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Users Card */}
        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden flex flex-col">
          <div className="p-4 sm:p-5 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
            <div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
                <Users size={16} className="text-indigo-500" />
                Latest User Registrations
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">Chronological identity provisioning events</p>
            </div>
            <Link to="/users" className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1">
              View All <ChevronRight size={13} />
            </Link>
          </div>
          <div className="flex-1 overflow-y-auto max-h-[360px] divide-y divide-slate-100 dark:divide-slate-800">
            {activity.recent_users?.length > 0 ? (
              activity.recent_users.map(user => (
                <div key={user.id} className="p-3.5 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs shadow-sm flex-shrink-0">
                      {user.firstname?.[0] || 'U'}{user.lastname?.[0] || ''}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                        {user.firstname} {user.lastname}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate font-mono">
                        {user.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end space-y-1 flex-shrink-0 ml-3">
                    <Badge variant={user.status === 1 ? 'success' : 'warning'} className="text-[10px] px-2 py-0.5">
                      {user.status === 1 ? 'Active' : 'Inactive'}
                    </Badge>
                    <span className="text-[10px] text-slate-400 dark:text-slate-500" title={formatDate(user.created_at)}>
                      {formatRelativeTime(user.created_at)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-sm text-slate-400">
                No recent identity signups found.
              </div>
            )}
          </div>
        </div>

        {/* Recent Tenants Card */}
        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden flex flex-col">
          <div className="p-4 sm:p-5 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
            <div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
                <Building2 size={16} className="text-cyan-500" />
                Active Organizations & Tenants
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">Multi-tenant workspace provisionings</p>
            </div>
            <Link to="/tenants" className="text-xs font-bold text-cyan-600 dark:text-cyan-400 hover:underline flex items-center gap-1">
              View All <ChevronRight size={13} />
            </Link>
          </div>
          <div className="flex-1 overflow-y-auto max-h-[360px] divide-y divide-slate-100 dark:divide-slate-800">
            {activity.recent_tenants?.length > 0 ? (
              activity.recent_tenants.map(tenant => (
                <div key={tenant.id} className="p-3.5 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className="h-9 w-9 rounded-xl bg-cyan-50 dark:bg-cyan-950/60 border border-cyan-200 dark:border-cyan-800 flex items-center justify-center text-cyan-600 dark:text-cyan-400 font-semibold flex-shrink-0">
                      <Building2 size={16} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                        {tenant.name}
                      </p>
                      <p className="text-[11px] text-slate-400 dark:text-slate-500 font-mono truncate">
                        ID: {tenant.id}
                      </p>
                    </div>
                  </div>
                  <Link 
                    to="/tenants"
                    className="text-xs font-semibold text-cyan-600 dark:text-cyan-400 hover:underline flex-shrink-0 ml-2"
                  >
                    Inspect
                  </Link>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-sm text-slate-400">
                No recent tenant organizations found.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;